import {
    ChangeDetectorRef,
    Component,
    ElementRef,
    NgZone,
    OnDestroy,
    OnInit,
    QueryList,
    Renderer2,
    ViewChild,
    ViewChildren
} from '@angular/core';
import {
    debounceTime,
    distinctUntilChanged,
    filter,
    map,
    skip,
    switchMap,
    take,
    takeUntil,
    tap,
    throttleTime
} from 'rxjs/operators';
import {ProjectService} from 'projects/app-misc/src/app/modules/project-misc/data/project/project.service';
import {MatDialog} from '@angular/material/dialog';
import {interval, merge, Subject, Subscription, timer} from 'rxjs';
import {LocalStorage} from 'ngx-webstorage';
import {DeviceDetectorService} from 'ngx-device-detector';
import {WsService} from 'projects/app-misc/src/app/modules/data/ws.service';
import {WsUpdateResponce} from 'projects/app-misc/src/app/modules/data/ws-update-responce';
import {TrelloCard} from 'projects/app-misc/src/app/modules/trello-misc/data/trello-card';
import {Project} from 'projects/app-misc/src/app/modules/project-misc/data/project/project';
import {KanbanColumn} from '../../data/kanban-column';
import {KanbanColumnComponent} from '../kanban-column/kanban-column.component';
import {KanbanBoardService} from '../../data/kanban-board.service';
import {KanbanBoardFilters, KanbanBoardStore} from '../../data/kanban-board.store';
import {KanbanBoardQuery} from '../../data/kanban-board.query';
import {ProjectWindowComponent} from '../../../project/view/project-window/project-window.component';
import {RxJSUnsubscribe} from 'projects/core/src/lib/core/utils/decorators/rxjs-unsubscribe-decorator';
import {RxJSUnsubscribable} from 'projects/core/src/lib/core/utils/interfaces/rxjs-unsubscribable';
import {ProjectInfoComponent} from 'projects/mobile/src/app/modules/project/view/project-info/project-info.component';

@Component({
    selector: 'app-kanban-board',
    templateUrl: './kanban-board.component.html',
    styleUrls: ['./kanban-board.component.scss'],
    providers: [DeviceDetectorService],
    // changeDetection: ChangeDetectionStrategy.OnPush
})
@RxJSUnsubscribe
export class KanbanBoardComponent implements OnInit, OnDestroy, RxJSUnsubscribable {
    getCols: Subscription;

    deviceInfo: string;

    columns$ = this.kanbanBoardQuery.selectAll();
    columns: KanbanColumn[] = []

    loadingSpinner = false;

    @LocalStorage()
    localScroll: number;

    @ViewChild('boardWrapper', {static: true})
    boardWrapper: ElementRef;

    @ViewChildren(KanbanColumnComponent, {read: ElementRef})
    columnEls: QueryList<ElementRef>;

    kanbanBoardFilter: KanbanBoardFilters

    filtersSub: Subscription;

    detectTimer: Subscription;

    readonly autoUpdateInterval = 60 * 3 * 1000;

    // readonly autoUpdateInterval = 400;

    readonly scrollOnDragStartArea = 20;
    readonly scrollOnDragStep = 250;
    private isProcessing = false;

    protected scrollSubj = new Subject<number>();
    wsSub: Subscription;

    constructor(
        protected deviceService: DeviceDetectorService,
        protected projectService: ProjectService,
        protected kanbanBoardService: KanbanBoardService,
        protected kanbanBoardStore: KanbanBoardStore,
        protected kanbanBoardQuery: KanbanBoardQuery,
        protected changeDetectorRef: ChangeDetectorRef,
        protected el: ElementRef,
        protected ngZone: NgZone,
        protected dialog: MatDialog,
        protected renderer: Renderer2,
        protected wsService: WsService
    ) {
        this.changeDetectorRef.detach();
    }

    ngOnInit() {
        this.loadColumns()

        this.detectDevice();

        this.initScrollListening()

        this.initColumnsChangeListening()

        this.initRealTImeUpdates()

        this.subToNewProject();

        this.subToColumnsChanges();

        this.subToBoardChanges();

        this.initFiltersOnPageLoad();
    }

    initFiltersOnPageLoad() {
        // Wait for filter components to initialize and apply their stored values
        timer(200).pipe(
            switchMap(() => this.kanbanBoardQuery.filter$),
            take(1)
        ).subscribe(filter => {
            if (filter && (filter.managers || filter.customers || filter.paymentStatuses || filter.columns)) {
                this.kanbanBoardFilter = filter;
                this.initGetCustomColumns(this.kanbanBoardFilter);
            }
        });
    }

    subToNewProject() {
        this.kanbanBoardQuery.newProject$.pipe(
            takeUntil(this.getUnsubFromAllSubs())
        ).subscribe(newProject => {
            if (newProject) {
                this.createNewProject(newProject)
                this.kanbanBoardStore.newProject(null)
            }
        })
    }

    subToColumnsChanges() {
        this.getCols = merge(
            interval(this.autoUpdateInterval),
            this.kanbanBoardQuery.filter$.pipe(
                // debounceTime(500),
                tap(kanbanBoardFilter => {
                    this.kanbanBoardFilter = kanbanBoardFilter;
                }))
        ).pipe(
            takeUntil(this.getUnsubFromAllSubs())
        ).subscribe(() => {
            if (!this.isProcessing) {
                this.isProcessing = true;
                this.initGetCustomColumns(this.kanbanBoardFilter);
                setTimeout(() => {
                    this.isProcessing = false;
                }, 50);
            }
        })
    }

    subToBoardChanges() {
        this.kanbanBoardQuery.updateBoard$.subscribe(() => {
            timer(500).pipe(
                takeUntil(this.getUnsubFromAllSubs())
            ).subscribe(() => {
                this.changeDetectorRef.detectChanges();
            })
        })
    }

    initGetCustomColumns(kanbanBoardFilter: KanbanBoardFilters) {
        this.changeDetectorRef.detectChanges();


        if (this.filtersSub && !this.filtersSub.closed) {
            this.filtersSub.unsubscribe();
        }

        this.filtersSub = this.kanbanBoardService.loadCustomColumns(kanbanBoardFilter).subscribe(() => {

            this.detectTimer = timer(150, 300).subscribe(() => {
                this.changeDetectorRef.detectChanges();
            })

            timer(1000).subscribe(() => {
                if (this.detectTimer) {
                    this.detectTimer.unsubscribe();
                }
            })
        })
    }

    loadColumns() {
        let isScrolled = false;

        this.columns$
            .pipe(
                takeUntil(this.getUnsubFromAllSubs()),
                skip(1)
            )
            .subscribe(columns => {
                this.columns = columns
                this.changeDetectorRef.detectChanges();
                if (!isScrolled) {
                    (this.boardWrapper.nativeElement as HTMLElement).scrollLeft = this.localScroll;
                    isScrolled = true;
                }
            });
    }

    initColumnsChangeListening() {
        this.kanbanBoardQuery
            .activeColumn
            .pipe(
                takeUntil(this.getUnsubFromAllSubs()),
                filter(activeColumn => activeColumn && activeColumn.id > 0),
                switchMap(activeColumn => {
                    return this.columns$
                        .pipe(
                            take(1),
                            map(columns => columns.findIndex(c => c.id === activeColumn.id)),
                            tap(() => {
                                this.changeDetectorRef.detectChanges();
                            })
                        )
                })
            )
            .subscribe(activeColumnIndex => {
                const columnRef = this.columnEls.toArray()[activeColumnIndex]

                if (columnRef) {
                    const columnEl = columnRef.nativeElement as HTMLElement;
                    const columnRect = columnEl.getBoundingClientRect();

                    const boardWrapperEl = this.boardWrapper.nativeElement as HTMLElement;
                    const boardWrapperRect = boardWrapperEl.getBoundingClientRect();

                    if (columnRect.left < 0 || columnRect.right > boardWrapperEl.scrollLeft + boardWrapperRect.width) {
                        // Work well on changing project list in project window
                        // But doesn't work *with* animation on project creation.
                        timer(250)
                            .subscribe(() => {
                                columnEl.scrollIntoView({behavior: 'smooth', block: 'center'});
                            })

                    }
                }

            })

        this.kanbanBoardService
            .projectDragMove
            .action
            .pipe(
                takeUntil(this.getUnsubFromAllSubs()),
                throttleTime(250),
                filter(event => event.pointerPosition.x <= this.scrollOnDragStartArea
                    || event.pointerPosition.x >= (this.boardWrapper.nativeElement as HTMLElement).clientWidth - this.scrollOnDragStartArea)
            )
            .subscribe(event => {
                const boardWrapperEl = this.boardWrapper.nativeElement as HTMLElement;
                boardWrapperEl.scrollBy({
                    left: this.scrollOnDragStep * (event.pointerPosition.x <= this.scrollOnDragStartArea ? -1 : 1),
                    behavior: 'smooth'
                });
            })
    }

    initScrollListening() {
        this.scrollSubj
            .pipe(
                takeUntil(this.getUnsubFromAllSubs()),
                debounceTime(250),
                distinctUntilChanged()
            )
            .subscribe(xScroll => {
                this.localScroll = xScroll
                // this.changeDetectorRef.detectChanges();
            })
        this.scrollSubj
            .pipe(
                takeUntil(this.getUnsubFromAllSubs()),
                distinctUntilChanged()
            )
            .subscribe(() => {
                this.changeDetectorRef.detectChanges();
            })

    }

    // initAutoUpdate() {
    // 	timer(0, this.autoUpdateInterval)
    // 		.pipe(
    // 			takeUntil(this.getUnsubFromAllSubs())
    // 		)
    // 		.subscribe(emit => {
    // 			this.kanbanBoardService.loadColumns()
    // 		})
    // }

    initRealTImeUpdates() {
        this.wsSub = this.wsService
            .listenUntilDestroyed<WsUpdateResponce<TrelloCard>>('/topic/update/model.trello.TrelloCard', this)
            .pipe(
                filter(updateInfo => typeof updateInfo.entity.list !== 'undefined')
            )
            .subscribe(updateInfo => {
                if (updateInfo.action === 'CREATE') {
                    this.emitProjectInsert(updateInfo.entity.id, updateInfo.entity.list.id)
                }
                if (updateInfo.action === 'DELETE') {
                    this.emitProjectRemove(updateInfo.entity.id, updateInfo.entity.list.id)
                }
                if (updateInfo.action === 'UPDATE') {
                    if (typeof updateInfo.oldEntity.list !== 'undefined') {
                        this.emitProjectUpdate(updateInfo.entity.id, updateInfo.entity.list.id, updateInfo.oldEntity.list.id)
                    }
                }
            })
    }

    emitProjectMove(cardId: number, columnId: number) {
        this.projectService.getProjectByCardId(cardId).subscribe(project => {
            this.kanbanBoardService.projectRemove.emit({
                columnId,
                projectId: project.id
            });
        })
    }

    emitProjectInsert(cardId: number, columnId: number) {
        console.log('SOCKET UPDATE')
        this.projectService.getProjectByCardId(cardId).subscribe(project => {
            this.kanbanBoardService.projectInsert.emit({
                columnId,
                project,
                index: project.card.pos
            });
        })
    }

    emitProjectRemove(cardId: number, columnId: number) {
        console.log('SOCKET UPDATE')
        this.projectService.getProjectByCardId(cardId).subscribe(project => {
            const foundedColumn = this.columns.find(column => column.id === columnId)
            const foundedCard = foundedColumn.projectsList.find(fProject => fProject.id === project.id)
            if (foundedCard) {
                this.kanbanBoardService.projectRemove.emit({
                    columnId,
                    projectId: project.id
                });
            }
        })
    }

    emitProjectUpdate(cardId: number, columnId: number, oldColumnId: number) {
        if (columnId === oldColumnId) {
            return
        }

        this.projectService.getProjectByCardId(cardId).subscribe(project => {

            const foundedColumn = this.columns.find(column => column.id === oldColumnId)
            const foundedCard = foundedColumn.projectsList.find(fProject => fProject.id === project.id)
            if (foundedCard) {
                this.kanbanBoardService.projectRemove.emit({
                    columnId: oldColumnId,
                    projectId: project.id
                });
            }

            this.kanbanBoardService.projectInsert.emit({
                columnId,
                project,
                index: project.card.pos
            });
        })
    }

    createNewProject(newProject: Project) {
        this.changeDetectorRef.detectChanges();
        this.openProjectWindow(newProject.id);
        this.kanbanBoardStore.updateActiveColumnId(newProject.card.list.id)
    }

    openProjectWindow(projectId: number) {
        let dialogRef
        if (this.deviceService.isMobile() || this.deviceService.isTablet()) {
            dialogRef = this.dialog.open(ProjectInfoComponent, {
                data: {
                    projectNumber: projectId
                },
                panelClass: 'project-info-dialog'
            });
        } else {
            dialogRef = this.dialog.open(ProjectWindowComponent, {
                data: {
                    projectNumber: projectId,
                },
                panelClass: 'project-window-dialog'
            });
        }

        dialogRef.afterClosed().subscribe(() => {
        });
    }

    startScrollOnDrag(boardWrapper: HTMLElement, event: MouseEvent) {
        const path = event.composedPath() as HTMLElement[]

        if (path) {
            for (let i = 0; i < path.length - 3; i++) {
                if (path[i].closest('.card-wrapper')) {
                    return
                }
            }
        }

        this.ngZone.runOutsideAngular(() => {
            const startX = event.pageX - 7;
            const scrollLeft = boardWrapper.scrollLeft;

            let stopListenMouseMove: () => void;
            let stopListenMouseUp: () => void;
            let stopListenMouseLeave: () => void;

            const stopListenAll = () => {
                stopListenMouseMove();
                stopListenMouseUp();
                stopListenMouseLeave();
            }

            stopListenMouseMove = this.renderer.listen(boardWrapper, 'mousemove', (mouseMoveEvt: MouseEvent) => {
                mouseMoveEvt.preventDefault();

                const x = mouseMoveEvt.pageX - 7;
                const walk = (x - startX);
                boardWrapper.scrollLeft = scrollLeft - walk;
            });

            stopListenMouseUp = this.renderer.listen(boardWrapper, 'mouseup', () => {
                stopListenAll();
            });


            stopListenMouseLeave = this.renderer.listen(boardWrapper, 'mouseleave', () => {
                stopListenAll();
            });

        });
    }

    onScroll(): void {
        const boardWrapperEl = this.boardWrapper.nativeElement as HTMLElement
        this.scrollSubj.next(boardWrapperEl.scrollLeft)
    }

    trackById(index: number, column: KanbanColumn) {
        return column.id;
    }

    detectDevice() {
        if (this.deviceService.isMobile()) {
            this.deviceInfo = 'isMobile'
        }
        if (this.deviceService.isDesktop()) {
            this.deviceInfo = 'isDesktop'
        }
        if (this.deviceService.isTablet()) {
            this.deviceInfo = 'isTablet'
        }
    }

    ngOnDestroy() {
        if (this.detectTimer) {
            this.detectTimer.unsubscribe();
            this.detectTimer = null
        }

        if (this.filtersSub) {
            this.filtersSub.unsubscribe();
        }

        if (this.getCols) {
            this.getCols.unsubscribe()
            this.getCols = null
        }

        if (this.wsSub) {
            this.wsSub.unsubscribe()
            this.wsSub = null;
        }
    }

    getUnsubFromAllSubs(): Subject<boolean> {
        return null
    }

}
